import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowLeft,
  Shield,
  Users,
  Plus,
  Edit,
  Trash2,
  Lock,
  History,
  UserPlus,
  Key,
  UserX,
  UserCheck,
  LogIn,
  Copy,
  RefreshCw,
  MapPin,
  LayoutGrid,
  Star,
  Save,
} from "lucide-react";
import NotificationBell from "./NotificationBell";
import UserMenu from "./UserMenu";
import { SAFETY_CATEGORIES, SAFETY_MODULES, catColor } from "../safety/modules";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

function RoleManagement({ user, onLogout, onImpersonate }) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("roles");
  
  // Permissions for current user
  const [userPermissions, setUserPermissions] = useState([]);
  
  // Roles state
  const [roles, setRoles] = useState([]);
  const [permissionsByCategory, setPermissionsByCategory] = useState({});
  const [loadingRoles, setLoadingRoles] = useState(true);
  
  // Users state
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  
  // Audit log state
  const [auditLog, setAuditLog] = useState([]);
  const [loadingAudit, setLoadingAudit] = useState(true);

  // Zones state (admin-managed stock-count zones)
  const [zones, setZones] = useState([]);
  const [newZoneName, setNewZoneName] = useState("");
  
  // Dialogs
  const [showCreateRoleDialog, setShowCreateRoleDialog] = useState(false);
  const [showEditRoleDialog, setShowEditRoleDialog] = useState(false);
  const [showAssignRoleDialog, setShowAssignRoleDialog] = useState(false);
  const [showCreateUserDialog, setShowCreateUserDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  
  // Form state
  const [newRole, setNewRole] = useState({ name: "", description: "", permissions: [] });
  const [editingRole, setEditingRole] = useState({ name: "", description: "", permissions: [] });
  const [newUser, setNewUser] = useState({ email: "", password: "", name: "", role_ids: [] });
  const [newPassword, setNewPassword] = useState("");
  const [revealedPassword, setRevealedPassword] = useState(null);
  
  // Permission check helpers
  const hasPermission = (perm) => userPermissions.includes(perm);
  const canCreateRole = hasPermission("role.create");
  const canEditRole = hasPermission("role.edit");
  const canDeleteRole = hasPermission("role.delete");
  const canViewUsers = hasPermission("user.view");
  const canCreateUser = hasPermission("user.create");
  const canEditUser = hasPermission("user.edit");
  const canDeleteUser = hasPermission("user.delete");
  const canAssignRoles = hasPermission("user.assign_roles");
  const canViewAudit = hasPermission("audit.view");
  const isSuperAdmin = (user?.role_ids || []).includes("role-super-admin") || user?.role === "ADMIN";
  
  useEffect(() => {
    fetchUserPermissions();
    fetchRoles();
    fetchPermissions();
    fetchUsers();
    fetchAuditLog();
    fetchZones();
  }, []);

  const fetchZones = async () => {
    try {
      const res = await axios.get(`${API}/stock-count/zones`, getAuthHeaders());
      setZones(res.data.zones || []);
    } catch (error) {
      console.error("Failed to load zones:", error);
    }
  };

  const [pinCfg, setPinCfg] = useState({ global: [], by_role: {}, hidden_categories: [] });
  const [pinScope, setPinScope] = useState("global");
  const [savingPins, setSavingPins] = useState(false);
  useEffect(() => {
    axios.get(`${API}/safety/default-modules`, getAuthHeaders())
      .then((r) => setPinCfg({ global: r.data.global || [], by_role: r.data.by_role || {}, hidden_categories: r.data.hidden_categories || [] }))
      .catch(() => {});
  }, []);
  const scopeList = pinScope === "global" ? (pinCfg.global || []) : (pinCfg.by_role?.[pinScope] || []);
  const toggleDefaultPin = (key) => setPinCfg((c) => {
    const cur = pinScope === "global" ? (c.global || []) : (c.by_role?.[pinScope] || []);
    const next = cur.includes(key) ? cur.filter((k) => k !== key) : [...cur, key];
    if (pinScope === "global") return { ...c, global: next };
    return { ...c, by_role: { ...(c.by_role || {}), [pinScope]: next } };
  });
  const toggleHiddenCat = (catKey) => setPinCfg((c) => {
    const cur = c.hidden_categories || [];
    return { ...c, hidden_categories: cur.includes(catKey) ? cur.filter((k) => k !== catKey) : [...cur, catKey] };
  });
  const saveDefaultPins = async () => {
    setSavingPins(true);
    try {
      await axios.put(`${API}/safety/default-modules`, {
        global: pinCfg.global || [],
        by_role: pinCfg.by_role || {},
        hidden_categories: pinCfg.hidden_categories || [],
      }, getAuthHeaders());
      toast.success("Safety Home defaults saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSavingPins(false); }
  };

  const handleCreateZone = async (e) => {
    e.preventDefault();
    const name = newZoneName.trim();
    if (!name) return;
    try {
      await axios.post(`${API}/stock-count/zones`, { name }, getAuthHeaders());
      toast.success(`Zone "${name}" created`);
      setNewZoneName("");
      fetchZones();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create zone");
    }
  };

  const handleDeleteZone = async (zone) => {
    if (!window.confirm(`Delete zone "${zone.name}"? Operators will no longer be able to select it.`)) return;
    try {
      await axios.delete(`${API}/stock-count/zones/${zone.id}`, getAuthHeaders());
      toast.success(`Zone "${zone.name}" removed`);
      fetchZones();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete zone");
    }
  };
  
  const fetchUserPermissions = async () => {
    try {
      const res = await axios.get(`${API}/auth/me/permissions`, getAuthHeaders());
      setUserPermissions(res.data.effective_permissions || []);
    } catch (error) {
      console.error("Failed to load user permissions:", error);
    }
  };
  
  const fetchRoles = async () => {
    try {
      const res = await axios.get(`${API}/roles`, getAuthHeaders());
      setRoles(res.data.roles || []);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("You don't have permission to view roles");
      } else {
        toast.error("Failed to load roles");
      }
    } finally {
      setLoadingRoles(false);
    }
  };
  
  const fetchPermissions = async () => {
    try {
      const res = await axios.get(`${API}/permissions`, getAuthHeaders());
      setPermissionsByCategory(res.data.by_category || {});
    } catch (error) {
      console.error("Failed to load permissions:", error);
    }
  };
  
  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API}/users`, getAuthHeaders());
      setUsers(res.data.users || []);
    } catch (error) {
      if (error.response?.status !== 403) {
        toast.error("Failed to load users");
      }
    } finally {
      setLoadingUsers(false);
    }
  };
  
  const fetchAuditLog = async () => {
    try {
      const res = await axios.get(`${API}/audit/rbac?limit=50`, getAuthHeaders());
      setAuditLog(res.data.audit_log || []);
    } catch (error) {
      // Silently fail
    } finally {
      setLoadingAudit(false);
    }
  };
  
  const handleCreateRole = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/roles`, newRole, getAuthHeaders());
      toast.success("Role created successfully");
      setShowCreateRoleDialog(false);
      setNewRole({ name: "", description: "", permissions: [] });
      fetchRoles();
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create role");
    }
  };
  
  const handleUpdateRole = async (e) => {
    e.preventDefault();
    if (!selectedRole) return;
    
    try {
      await axios.put(`${API}/roles/${selectedRole.id}`, editingRole, getAuthHeaders());
      toast.success("Role updated successfully");
      setShowEditRoleDialog(false);
      setSelectedRole(null);
      fetchRoles();
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update role");
    }
  };
  
  const handleDeleteRole = async (roleId) => {
    if (!window.confirm("Are you sure you want to delete this role?")) return;
    
    try {
      await axios.delete(`${API}/roles/${roleId}`, getAuthHeaders());
      toast.success("Role deleted successfully");
      fetchRoles();
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete role");
    }
  };
  
  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/users`, newUser, getAuthHeaders());
      toast.success("User created successfully");
      setShowCreateUserDialog(false);
      setNewUser({ email: "", password: "", name: "", role_ids: [] });
      fetchUsers();
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create user");
    }
  };
  
  const handleAssignRolesToUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    
    const currentUser = users.find(u => u.id === selectedUser);
    const selectedRoleIds = currentUser?.role_ids || [];
    
    try {
      await axios.put(
        `${API}/users/${selectedUser}/roles`,
        { role_ids: selectedRoleIds },
        getAuthHeaders()
      );
      toast.success("Roles assigned successfully");
      setShowAssignRoleDialog(false);
      setSelectedUser(null);
      fetchUsers();
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign roles");
    }
  };
  
  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedUser || !newPassword) return;
    
    try {
      await axios.post(
        `${API}/users/${selectedUser}/reset-password?new_password=${encodeURIComponent(newPassword)}`,
        {},
        getAuthHeaders()
      );
      // Reveal the password once so the admin can copy & share it. We never store
      // or fetch plaintext — this is simply the value the admin just set.
      setRevealedPassword(newPassword);
      toast.success("Password set successfully");
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    }
  };

  const generatePassword = () => {
    const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789@#%";
    const arr = new Uint32Array(14);
    (window.crypto || window.msCrypto).getRandomValues(arr);
    let p = "";
    for (let i = 0; i < arr.length; i++) p += chars[arr[i] % chars.length];
    setNewPassword(p);
    setRevealedPassword(null);
  };

  const closeResetPasswordDialog = () => {
    setShowResetPasswordDialog(false);
    setSelectedUser(null);
    setNewPassword("");
    setRevealedPassword(null);
  };
  
  const handleDeactivateUser = async (userId) => {
    if (!window.confirm("Are you sure you want to deactivate this user?")) return;
    
    try {
      await axios.delete(`${API}/users/${userId}`, getAuthHeaders());
      toast.success("User deactivated successfully");
      fetchUsers();
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to deactivate user");
    }
  };
  
  const handleReactivateUser = async (userId) => {
    if (!window.confirm("Are you sure you want to reactivate this user?")) return;
    
    try {
      await axios.put(`${API}/users/${userId}?is_active=true`, {}, getAuthHeaders());
      toast.success("User reactivated successfully");
      fetchUsers();
      fetchAuditLog();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate user");
    }
  };

  const handleImpersonateUser = async (userId) => {
    try {
      const res = await axios.post(
        `${API}/admin/impersonate/${userId}`,
        {},
        getAuthHeaders()
      );
      if (onImpersonate) {
        onImpersonate(res.data.user, res.data.access_token);
      }
      toast.success(`Now viewing as ${res.data.user.name || res.data.user.email}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to impersonate user");
    }
  };

  const handleUpdateDefaultModule = async (userId, defaultModule) => {
    try {
      await axios.put(`${API}/users/${userId}/default-module`, {
        default_module: defaultModule
      }, getAuthHeaders());
      toast.success("Default module updated");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update default module");
    }
  };
  
  const openEditRoleDialog = (role) => {
    setSelectedRole(role);
    setEditingRole({
      name: role.name,
      description: role.description || "",
      permissions: [...role.permissions]
    });
    setShowEditRoleDialog(true);
  };
  
  const openAssignRoleDialog = (userId) => {
    setSelectedUser(userId);
    setShowAssignRoleDialog(true);
  };
  
  const openResetPasswordDialog = (userId) => {
    setSelectedUser(userId);
    setNewPassword("");
    setRevealedPassword(null);
    setShowResetPasswordDialog(true);
  };
  
  const toggleUserRole = (userId, roleId) => {
    setUsers(prevUsers => prevUsers.map(u => {
      if (u.id !== userId) return u;
      const roleIds = u.role_ids || [];
      if (roleIds.includes(roleId)) {
        return { ...u, role_ids: roleIds.filter(r => r !== roleId) };
      } else {
        return { ...u, role_ids: [...roleIds, roleId] };
      }
    }));
  };
  
  const toggleNewUserRole = (roleId) => {
    if (newUser.role_ids.includes(roleId)) {
      setNewUser({ ...newUser, role_ids: newUser.role_ids.filter(r => r !== roleId) });
    } else {
      setNewUser({ ...newUser, role_ids: [...newUser.role_ids, roleId] });
    }
  };
  
  const categoryLabels = {
    document: "Document Management",
    email: "Email",
    project: "Project Management",
    client: "Client Management",
    admin: "Administration"
  };
  
  const categoryIcons = {
    document: "📄",
    email: "📧",
    project: "📁",
    client: "🏢",
    admin: "⚙️"
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="role-management-page">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate("/select-module")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl font-semibold flex items-center gap-2">
                <Shield className="h-5 w-5 text-blue-600" />
                Administration
              </h1>
              <p className="text-sm text-gray-500">Roles, permissions & user management</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <NotificationBell />
            <UserMenu user={user} onLogout={onLogout} />
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6">
            <TabsTrigger value="roles" className="flex items-center gap-2" data-testid="roles-tab">
              <Shield className="h-4 w-4" />
              Roles
            </TabsTrigger>
            <TabsTrigger value="users" className="flex items-center gap-2" data-testid="users-tab">
              <Users className="h-4 w-4" />
              Users
            </TabsTrigger>
            <TabsTrigger value="zones" className="flex items-center gap-2" data-testid="zones-tab">
              <MapPin className="h-4 w-4" />
              Zones
            </TabsTrigger>
            <TabsTrigger value="safety-home" className="flex items-center gap-2" data-testid="safety-home-tab">
              <LayoutGrid className="h-4 w-4" />
              Safety Home
            </TabsTrigger>
            {canViewAudit && (
              <TabsTrigger value="audit" className="flex items-center gap-2" data-testid="audit-tab">
                <History className="h-4 w-4" />
                Audit Log
              </TabsTrigger>
            )}
          </TabsList>
          
          {/* Roles Tab */}
          <TabsContent value="roles">
            <div className="bg-white rounded-lg border p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-medium">System Roles</h2>
                {canCreateRole && (
                  <Button onClick={() => setShowCreateRoleDialog(true)} data-testid="create-role-btn">
                    <Plus className="h-4 w-4 mr-2" />
                    Create Role
                  </Button>
                )}
              </div>
              
              {loadingRoles ? (
                <div className="text-center py-8 text-gray-500">Loading roles...</div>
              ) : roles.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No roles found</div>
              ) : (
                <div className="space-y-4">
                  {roles.map(role => (
                    <div 
                      key={role.id} 
                      className="border rounded-lg p-4 hover:bg-gray-50"
                      data-testid={`role-card-${role.id}`}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="font-medium text-gray-900">{role.name}</h3>
                            {role.is_system && (
                              <Badge variant="secondary" className="text-xs">
                                <Lock className="h-3 w-3 mr-1" />
                                System
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{role.description}</p>
                          <div className="flex flex-wrap gap-1 mt-3">
                            {role.permissions.slice(0, 8).map(perm => (
                              <Badge key={perm} variant="outline" className="text-xs">
                                {perm}
                              </Badge>
                            ))}
                            {role.permissions.length > 8 && (
                              <Badge variant="outline" className="text-xs bg-gray-100">
                                +{role.permissions.length - 8} more
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          {canEditRole && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openEditRoleDialog(role)}
                              data-testid={`edit-role-${role.id}`}
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                          )}
                          {canDeleteRole && !role.is_system && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteRole(role.id)}
                              data-testid={`delete-role-${role.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
          
          {/* Users Tab */}
          <TabsContent value="users">
            <div className="bg-white rounded-lg border p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-medium">User Management</h2>
                {canCreateUser && (
                  <Button onClick={() => setShowCreateUserDialog(true)} data-testid="create-user-btn">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Create User
                  </Button>
                )}
              </div>
              
              {loadingUsers ? (
                <div className="text-center py-8 text-gray-500">Loading users...</div>
              ) : users.length === 0 ? (
                <div className="text-center py-8 text-gray-500">No users found</div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Default Module</TableHead>
                      <TableHead>Assigned Roles</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map(u => (
                      <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                        <TableCell>
                          <div>
                            <div className="font-medium">{u.name || u.email}</div>
                            <div className="text-sm text-gray-500">{u.email}</div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {u.is_active !== false ? (
                            <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                              <UserCheck className="h-3 w-3 mr-1" />
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="bg-red-50 text-red-700 border-red-200">
                              <UserX className="h-3 w-3 mr-1" />
                              Inactive
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={u.default_module || "none"}
                            onValueChange={(value) => handleUpdateDefaultModule(u.id, value)}
                            data-testid={`default-module-${u.id}`}
                          >
                            <SelectTrigger className="w-[140px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No Default</SelectItem>
                              <SelectItem value="project">Project</SelectItem>
                              <SelectItem value="safety">Safety Equipment</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1">
                            {(u.roles || []).map(r => (
                              <Badge key={r.id} variant="secondary" className="text-xs">
                                {r.name}
                              </Badge>
                            ))}
                            {(!u.roles || u.roles.length === 0) && (
                              <span className="text-sm text-gray-400">No roles</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            {canAssignRoles && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openAssignRoleDialog(u.id)}
                                data-testid={`assign-roles-${u.id}`}
                              >
                                <Shield className="h-4 w-4 mr-1" />
                                Roles
                              </Button>
                            )}
                            {canEditUser && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => openResetPasswordDialog(u.id)}
                                data-testid={`reset-password-${u.id}`}
                              >
                                <Key className="h-4 w-4" />
                              </Button>
                            )}
                            {canDeleteUser && u.id !== user?.id && u.is_active !== false && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => handleDeactivateUser(u.id)}
                                data-testid={`deactivate-user-${u.id}`}
                              >
                                <UserX className="h-4 w-4" />
                              </Button>
                            )}
                            {canEditUser && u.is_active === false && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-green-600 hover:bg-green-50"
                                onClick={() => handleReactivateUser(u.id)}
                                data-testid={`reactivate-user-${u.id}`}
                              >
                                <UserCheck className="h-4 w-4" />
                              </Button>
                            )}
                            {isSuperAdmin && u.id !== user?.id && u.is_active !== false && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="text-pink-600 hover:bg-pink-50 border-pink-200"
                                onClick={() => handleImpersonateUser(u.id)}
                                data-testid={`impersonate-user-${u.id}`}
                              >
                                <LogIn className="h-4 w-4 mr-1" />
                                Login As
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>
          </TabsContent>
          
          {/* Zones Tab */}
          <TabsContent value="zones">
            <div className="bg-white rounded-lg border p-6">
              <div className="flex justify-between items-center mb-2">
                <h2 className="text-lg font-medium">Stock-Count Zones</h2>
              </div>
              <p className="text-sm text-gray-500 mb-6">
                Zones/aisles operators can select while doing a physical stock count on the mobile app.
                Zones can only be created here by an admin — not on the app.
              </p>

              <form onSubmit={handleCreateZone} className="flex gap-2 mb-6 max-w-md">
                <Input
                  value={newZoneName}
                  onChange={(e) => setNewZoneName(e.target.value)}
                  placeholder="New zone name (e.g. Warehouse A, Aisle 3)"
                  data-testid="new-zone-input"
                />
                <Button type="submit" data-testid="create-zone-btn">
                  <Plus className="h-4 w-4 mr-1" /> Add Zone
                </Button>
              </form>

              {zones.length === 0 ? (
                <p className="text-sm text-gray-400" data-testid="zones-empty">No zones yet. Add one above.</p>
              ) : (
                <div className="grid gap-2 max-w-md" data-testid="zones-list">
                  {zones.map((z) => (
                    <div key={z.id} data-testid={`zone-row-${z.id}`}
                      className="flex items-center justify-between border rounded-lg px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4 text-amber-500" />
                        <span className="font-medium">{z.name}</span>
                      </div>
                      <Button variant="ghost" size="icon" className="text-red-600 hover:text-red-700"
                        onClick={() => handleDeleteZone(z)} data-testid={`delete-zone-${z.id}`}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Safety Home Tab — org-wide default pinned modules + category visibility */}
          <TabsContent value="safety-home">
            <div className="bg-white rounded-lg border p-6" data-testid="safety-home-panel">
              <div className="flex items-start justify-between mb-1">
                <div>
                  <h2 className="text-lg font-medium flex items-center gap-2"><LayoutGrid className="h-5 w-5" /> Safety Home Defaults</h2>
                  <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                    Set the default <b>"My Modules"</b> pins users see before they personalize — globally or per role.
                    Personal pins always override defaults, and users only ever see modules their role can access.
                  </p>
                </div>
                <Button onClick={saveDefaultPins} disabled={savingPins} data-testid="save-default-pins-btn">
                  <Save className="h-4 w-4 mr-2" /> {savingPins ? "Saving…" : "Save"}
                </Button>
              </div>

              {/* Scope selector (#12 per-role) */}
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Editing defaults for:</span>
                <button onClick={() => setPinScope("global")} className={`text-sm rounded-full px-3 py-1.5 border ${pinScope === "global" ? "border-orange-400 bg-orange-50 text-orange-700 font-medium" : "border-gray-200 hover:bg-gray-50"}`} data-testid="pin-scope-global">
                  Everyone (global)
                </button>
                {roles.map((r) => (
                  <button key={r.id} onClick={() => setPinScope(r.id)} className={`text-sm rounded-full px-3 py-1.5 border ${pinScope === r.id ? "border-orange-400 bg-orange-50 text-orange-700 font-medium" : "border-gray-200 hover:bg-gray-50"}`} data-testid={`pin-scope-${r.id}`}>
                    {r.name}{(pinCfg.by_role?.[r.id]?.length || 0) > 0 ? ` (${pinCfg.by_role[r.id].length})` : ""}
                  </button>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-1">
                {pinScope === "global" ? "Applies to users whose role has no specific default set." : "Overrides the global default for users with this role."}
              </p>

              <div className="mt-4 space-y-5">
                {SAFETY_CATEGORIES.map((cat) => (
                  <div key={cat.key}>
                    <div className="text-xs font-semibold text-gray-600 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 rounded-sm inline-block" style={{ background: cat.color }} /> {cat.label}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-2">
                      {SAFETY_MODULES.filter((m) => m.cat === cat.key).map((m) => {
                        const Icon = m.icon;
                        const on = scopeList.includes(m.key);
                        return (
                          <button
                            key={m.key}
                            onClick={() => toggleDefaultPin(m.key)}
                            className={`flex items-center gap-2 border rounded-lg px-3 py-2.5 text-left transition ${on ? "border-orange-400 bg-orange-50" : "border-gray-200 hover:bg-gray-50"}`}
                            data-testid={`default-pin-${m.key}`}
                          >
                            <span className="w-7 h-7 rounded-md flex items-center justify-center text-white shrink-0" style={{ background: catColor(m.cat) }}><Icon className="w-3.5 h-3.5" /></span>
                            <span className="text-sm font-medium truncate flex-1">{m.label}</span>
                            <Star className={`w-4 h-4 shrink-0 ${on ? "fill-orange-500 text-orange-500" : "text-gray-300"}`} />
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>

              {/* Category visibility (#13) */}
              <div className="mt-8 border-t pt-5">
                <h3 className="text-sm font-medium flex items-center gap-2">Category visibility</h3>
                <p className="text-xs text-gray-500 mt-1 mb-3">Hide an entire launchpad category org-wide, even for users who have access (e.g. hide "Reports & Admin" from the floor).</p>
                <div className="flex flex-wrap gap-2">
                  {SAFETY_CATEGORIES.map((cat) => {
                    const hidden = (pinCfg.hidden_categories || []).includes(cat.key);
                    return (
                      <button key={cat.key} onClick={() => toggleHiddenCat(cat.key)} className={`text-sm rounded-lg px-3 py-2 border flex items-center gap-2 ${hidden ? "border-red-300 bg-red-50 text-red-600" : "border-gray-200 hover:bg-gray-50"}`} data-testid={`hide-cat-${cat.key}`}>
                        <span className="w-2 h-2 rounded-sm inline-block" style={{ background: cat.color }} /> {cat.label}: <b>{hidden ? "Hidden" : "Visible"}</b>
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </TabsContent>

          
          {/* Audit Log Tab */}
          {canViewAudit && (
            <TabsContent value="audit">
              <div className="bg-white rounded-lg border p-6">
                <h2 className="text-lg font-medium mb-6">RBAC Audit Log</h2>
                
                {loadingAudit ? (
                  <div className="text-center py-8 text-gray-500">Loading audit log...</div>
                ) : auditLog.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">No audit entries found</div>
                ) : (
                  <div className="space-y-3">
                    {auditLog.map((entry, idx) => (
                      <div 
                        key={entry.id || idx} 
                        className="border rounded p-3 text-sm"
                        data-testid={`audit-entry-${idx}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <span className="font-medium text-blue-600">{entry.action}</span>
                            <span className="text-gray-500 ml-2">on {entry.target_type}</span>
                          </div>
                          <span className="text-xs text-gray-400">
                            {new Date(entry.timestamp).toLocaleString("en-IN", { timeZone: "Asia/Kolkata" })}
                          </span>
                        </div>
                        <div className="text-gray-600 mt-1">
                          by <span className="font-medium">{entry.actor_email}</span>
                        </div>
                        {entry.changes && Object.keys(entry.changes).length > 0 && (
                          <div className="mt-2 text-xs text-gray-500 bg-gray-50 p-2 rounded">
                            {JSON.stringify(entry.changes, null, 2).slice(0, 200)}
                            {JSON.stringify(entry.changes).length > 200 && "..."}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </TabsContent>
          )}
        </Tabs>
      </main>
      
      {/* Create Role Dialog */}
      <Dialog open={showCreateRoleDialog} onOpenChange={setShowCreateRoleDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create New Role</DialogTitle>
            <DialogDescription>
              Define a new role with specific permissions
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateRole}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="role-name">Role Name</Label>
                <Input
                  id="role-name"
                  value={newRole.name}
                  onChange={(e) => setNewRole({ ...newRole, name: e.target.value })}
                  placeholder="e.g., Document Reviewer"
                  required
                  data-testid="role-name-input"
                />
              </div>
              <div>
                <Label htmlFor="role-desc">Description</Label>
                <Input
                  id="role-desc"
                  value={newRole.description}
                  onChange={(e) => setNewRole({ ...newRole, description: e.target.value })}
                  placeholder="Brief description of this role"
                  data-testid="role-description-input"
                />
              </div>
              <div>
                <Label className="mb-2 block">Permissions</Label>
                <Accordion type="multiple" className="w-full">
                  {Object.entries(permissionsByCategory).map(([category, perms]) => (
                    <AccordionItem key={category} value={category}>
                      <AccordionTrigger className="text-sm">
                        <span className="flex items-center gap-2">
                          <span>{categoryIcons[category] || "📋"}</span>
                          {categoryLabels[category] || category}
                          <Badge variant="outline" className="ml-2">
                            {perms.filter(p => newRole.permissions.includes(p.id)).length}/{perms.length}
                          </Badge>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-2 pl-4">
                          {perms.map(perm => (
                            <div key={perm.id} className="flex items-start gap-2">
                              <Checkbox
                                id={`new-${perm.id}`}
                                checked={newRole.permissions.includes(perm.id)}
                                onCheckedChange={() => {
                                  const perms = newRole.permissions.includes(perm.id)
                                    ? newRole.permissions.filter(p => p !== perm.id)
                                    : [...newRole.permissions, perm.id];
                                  setNewRole({ ...newRole, permissions: perms });
                                }}
                                data-testid={`perm-checkbox-${perm.id}`}
                              />
                              <div className="flex-1">
                                <label htmlFor={`new-${perm.id}`} className="text-sm font-medium cursor-pointer">
                                  {perm.name}
                                </label>
                                <p className="text-xs text-gray-500">{perm.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateRoleDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-create-role">Create Role</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Edit Role Dialog */}
      <Dialog open={showEditRoleDialog} onOpenChange={setShowEditRoleDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Role: {selectedRole?.name}</DialogTitle>
            <DialogDescription>
              {selectedRole?.is_system ? "System role - only permissions can be modified" : "Modify role details and permissions"}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleUpdateRole}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="edit-role-name">Role Name</Label>
                <Input
                  id="edit-role-name"
                  value={editingRole.name}
                  onChange={(e) => setEditingRole({ ...editingRole, name: e.target.value })}
                  disabled={selectedRole?.is_system}
                  required
                  data-testid="edit-role-name-input"
                />
              </div>
              <div>
                <Label htmlFor="edit-role-desc">Description</Label>
                <Input
                  id="edit-role-desc"
                  value={editingRole.description}
                  onChange={(e) => setEditingRole({ ...editingRole, description: e.target.value })}
                  disabled={selectedRole?.is_system}
                  data-testid="edit-role-description-input"
                />
              </div>
              <div>
                <Label className="mb-2 block">Permissions</Label>
                <Accordion type="multiple" className="w-full">
                  {Object.entries(permissionsByCategory).map(([category, perms]) => (
                    <AccordionItem key={category} value={category}>
                      <AccordionTrigger className="text-sm">
                        <span className="flex items-center gap-2">
                          <span>{categoryIcons[category] || "📋"}</span>
                          {categoryLabels[category] || category}
                          <Badge variant="outline" className="ml-2">
                            {perms.filter(p => editingRole.permissions.includes(p.id)).length}/{perms.length}
                          </Badge>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-2 pl-4">
                          {perms.map(perm => (
                            <div key={perm.id} className="flex items-start gap-2">
                              <Checkbox
                                id={`edit-${perm.id}`}
                                checked={editingRole.permissions.includes(perm.id)}
                                onCheckedChange={() => {
                                  const perms = editingRole.permissions.includes(perm.id)
                                    ? editingRole.permissions.filter(p => p !== perm.id)
                                    : [...editingRole.permissions, perm.id];
                                  setEditingRole({ ...editingRole, permissions: perms });
                                }}
                              />
                              <div className="flex-1">
                                <label htmlFor={`edit-${perm.id}`} className="text-sm font-medium cursor-pointer">
                                  {perm.name}
                                </label>
                                <p className="text-xs text-gray-500">{perm.description}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditRoleDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-edit-role">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Create User Dialog */}
      <Dialog open={showCreateUserDialog} onOpenChange={setShowCreateUserDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>
              Add a new user to the system
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser}>
            <div className="space-y-4 py-4">
              <div>
                <Label htmlFor="user-email">Email</Label>
                <Input
                  id="user-email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  placeholder="user@example.com"
                  required
                  data-testid="user-email-input"
                />
              </div>
              <div>
                <Label htmlFor="user-name">Name</Label>
                <Input
                  id="user-name"
                  value={newUser.name}
                  onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                  placeholder="John Doe"
                  data-testid="user-name-input"
                />
              </div>
              <div>
                <Label htmlFor="user-password">Password</Label>
                <Input
                  id="user-password"
                  type="password"
                  value={newUser.password}
                  onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  data-testid="user-password-input"
                />
              </div>
              <div>
                <Label className="mb-2 block">Assign Roles</Label>
                <div className="space-y-2 max-h-[200px] overflow-y-auto border rounded p-2">
                  {roles.map(role => (
                    <div key={role.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`newuser-${role.id}`}
                        checked={newUser.role_ids.includes(role.id)}
                        onCheckedChange={() => toggleNewUserRole(role.id)}
                      />
                      <label htmlFor={`newuser-${role.id}`} className="text-sm cursor-pointer flex items-center gap-2">
                        {role.name}
                        {role.is_system && <Lock className="h-3 w-3 text-gray-400" />}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateUserDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-create-user">Create User</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Assign Roles Dialog */}
      <Dialog open={showAssignRoleDialog} onOpenChange={setShowAssignRoleDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assign Roles</DialogTitle>
            <DialogDescription>
              Select roles to assign to {users.find(u => u.id === selectedUser)?.email}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAssignRolesToUser}>
            <div className="space-y-3 py-4 max-h-[400px] overflow-y-auto">
              {roles.map(role => {
                const currentUser = users.find(u => u.id === selectedUser);
                const isAssigned = (currentUser?.role_ids || []).includes(role.id);
                
                return (
                  <div key={role.id} className="flex items-start gap-3 p-2 border rounded hover:bg-gray-50">
                    <Checkbox
                      id={`assign-${role.id}`}
                      checked={isAssigned}
                      onCheckedChange={() => toggleUserRole(selectedUser, role.id)}
                    />
                    <div className="flex-1">
                      <label htmlFor={`assign-${role.id}`} className="text-sm font-medium cursor-pointer flex items-center gap-2">
                        {role.name}
                        {role.is_system && <Lock className="h-3 w-3 text-gray-400" />}
                      </label>
                      <p className="text-xs text-gray-500">{role.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowAssignRoleDialog(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="submit-assign-roles">Save Assignments</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      
      {/* Reset Password Dialog */}
      <Dialog open={showResetPasswordDialog} onOpenChange={(o) => { if (!o) closeResetPasswordDialog(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Set / Reset Password</DialogTitle>
            <DialogDescription>
              Set a new password for {users.find(u => u.id === selectedUser)?.email}. Passwords are
              stored as one-way hashes and cannot be viewed later — copy it now if you need to share it.
            </DialogDescription>
          </DialogHeader>

          {revealedPassword ? (
            <div className="space-y-4 py-4" data-testid="revealed-password-panel">
              <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                <p className="text-xs font-medium text-emerald-700 mb-2">
                  New password (visible only now — copy & share it)
                </p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 select-all rounded bg-white px-3 py-2 font-mono text-sm border" data-testid="revealed-password-value">
                    {revealedPassword}
                  </code>
                  <Button
                    type="button"
                    size="icon"
                    variant="outline"
                    onClick={() => { navigator.clipboard.writeText(revealedPassword); toast.success("Copied to clipboard"); }}
                    data-testid="copy-password-btn"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              <DialogFooter>
                <Button type="button" onClick={closeResetPasswordDialog} data-testid="done-reset-password">
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            <form onSubmit={handleResetPassword}>
              <div className="space-y-4 py-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="new-password">New Password</Label>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={generatePassword}
                      data-testid="generate-password-btn"
                    >
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Generate strong password
                    </Button>
                  </div>
                  <Input
                    id="new-password"
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Type or generate a password"
                    required
                    className="font-mono"
                    data-testid="new-password-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={closeResetPasswordDialog}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="submit-reset-password">Set Password</Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default RoleManagement;
